import { G } from '@mobily/ts-belt';
import { z } from 'zod';

import {
  ZodCompanyIndustry,
  ZodCreateBusinessUserCompanyDirectors,
  ZodCreateBusinessUserCompanyUBOs,
} from '@Common/types/auth/create_business_user';
import {
  ZodCreateCustomerResponse,
  ZodUserInvestmentHorizon,
  ZodWMCustodyFee,
  ZodWMCustomerIFUData,
  ZodWMCustomerPepStatus,
} from '@Common/types/bo_wm/customers/create_customer';
import { ZodInvestingExperienceQuestionnaireV2 } from '@Common/types/bo_wm/customers/investing_experience_questionnaire_v2';
import { customNationalityValidation, ZodAddressInfo } from '@Common/types/questions/user_profile';
import { ZodUBODetailsV2 } from '@Common/types/ubo/details';
import { ZodCity } from '@Common/types/user/address';
import { ZodUserBirthdate } from '@Common/types/user/birthdate';
import { ZodUserFirstName, ZodUserLastName } from '@Common/types/user/legal_name';
import { ZodSEPABankAccount } from '@Common/types/zod/bank_account';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { ZodDateString } from '@Common/types/zod/zod-date-string';
import { ZodAppUserEmail } from '@Common/types/zod/zod-email';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodPhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodCreateBusinessUserCompanyUBOV2 = z.object({
  id: ZodTrimmedString,
  gender: z.enum(['male', 'female']).nullish(),
  firstname: ZodUserFirstName,
  lastname: ZodUserLastName,
  birthdate: ZodUserBirthdate,
  ifu: ZodWMCustomerIFUData.optional(),
  country_of_tax_residence: ZodCountryCode,
  nationality: ZodCountryCode,
  other_nationality: ZodCountryCode.nullish(),
  tax_number: ZodTrimmedString.nullish(),
  pep_status: ZodWMCustomerPepStatus.nullish(),
  home_address: ZodAddressInfo,
  not_us_person: z.boolean(),
});

export const ZodBusinessUserCompanyUBOV2 = z.object({
  ...ZodCreateBusinessUserCompanyUBOV2.omit({
    birthdate: true,
  }).shape,
  birthdate: ZodTrimmedString,
});

export const WM_CUSTOMER_SUPPORTED_LEGAL_CATEGORY_CODES = [
  '1000',
  '2210',
  '2220',
  '2310',
  '2320',
  '2385',
  '5410',
  '5470',
  '5485',
  '5498',
  '5499',
  '5505',
  '5510',
  '5515',
  '5520',
  '5522',
  '5525',
  '5530',
  '5531',
  '5532',
  '5542',
  '5543',
  '5546',
  '5547',
  '5548',
  '5551',
  '5552',
  '5553',
  '5554',
  '5555',
  '5558',
  '5559',
  '5560',
  '5570',
  '5585',
  '5599',
  '5605',
  '5610',
  '5615',
  '5620',
  '5622',
  '5625',
  '5630',
  '5631',
  '5632',
  '5642',
  '5643',
  '5646',
  '5647',
  '5648',
  '5651',
  '5652',
  '5653',
  '5654',
  '5655',
  '5658',
  '5659',
  '5660',
  '5670',
  '5685',
  '5699',
  '5710',
  '5720',
  '5770',
  '5785',
  '6540',
  '6571',
  '6572',
  '6573',
  '6574',
  '6575',
  '6576',
  '6577',
  '6578',
  '6585',
  '6589',
  '6597',
  '6598',
  '6599',
];

export const BusinessUserInvestmentObjectiveValues = [
  'grow_capital',
  'additional_income',
  'optimize_taxation',
  'future_project',
  'long_term_resilience',
  'transfer_capital',
] as const;

const ZodBusinessUserInvestmentObjective = z.enum(BusinessUserInvestmentObjectiveValues);

export type BusinessUserInvestmentObjectiveType = z.infer<
  typeof ZodBusinessUserInvestmentObjective
>;

export const ZodBusinessUserInvestmentObjectives = ZodBusinessUserInvestmentObjective.array();

export const ZodCreateBusinessCustomerAccountHolder = z.object({
  firstname: ZodUserFirstName,
  lastname: ZodUserLastName,
  gender: z.enum(['male', 'female']).nullish(),
  birthdate: ZodUserBirthdate,
  phone_number: ZodPhoneNumber,
  email: ZodAppUserEmail,
  city: ZodCity.optional(),
  country_of_tax_residence: z.enum(['FR']),
  nationality: ZodCountryCode,
  other_nationality: ZodCountryCode.nullish(),
  national_identifier: ZodTrimmedString.nullish(),
  tax_number: ZodTrimmedString.optional(),
  home_address: ZodAddressInfo.optional(),
  pep_status: ZodWMCustomerPepStatus.nullish(),
  investing_experience_questionnaire_v2: ZodInvestingExperienceQuestionnaireV2,
  custody_fee: ZodWMCustodyFee.optional(),
  ifu: ZodWMCustomerIFUData.optional(),
});

export const UBOsV2Data = z.object({
  pappers_data: z.array(ZodUBODetailsV2),
  ubos: z.array(ZodCreateBusinessUserCompanyUBOV2),
});

export const ZodBusinessCustomerInvestment = z.object({
  horizon: ZodUserInvestmentHorizon.nullable(),
  objectives: z.array(ZodBusinessUserInvestmentObjective).min(1).nullable(),
});

// Zod forbids `.pick()`/`.omit()`/`.partial()` on refined objects, so derive from this one
export const ZodWMBusinessUsTaxWithholdingAndReportingUnrefined = z.object({
  facta_status: z.enum(['active_nffe', 'passive_nffe']),
  basis_for_treaty_benefits: z.enum([
    'company_meets_ownership_and_base_erosion_test',
    'company_meets_derivative_benefits_test',
    'company_with_item_of_income_meets_active_trade_or_business_test',
    'no_lob_article_in_treaty',
  ]),
  special_rates_and_conditions: z.enum(['not_applicable', 'applicable_specify_below']),
  article_and_paragraph: ZodTrimmedString.nullish(),
  claimed_rate_of_withholding_percentage: z.number().min(0).max(100).nullish(),
  type_of_income: ZodTrimmedString.nullish(),
  reason_of_eligibility: ZodTrimmedString.max(300).nullish(),
});

export const ZodWMBusinessUsTaxWithholdingAndReporting =
  ZodWMBusinessUsTaxWithholdingAndReportingUnrefined.superRefine((data, ctx) => {
    const conditionalFields = [
      {
        key: 'article_and_paragraph',
        value: data.article_and_paragraph,
      },
      {
        key: 'claimed_rate_of_withholding_percentage',
        value: data.claimed_rate_of_withholding_percentage,
      },
      {
        key: 'type_of_income',
        value: data.type_of_income,
      },
      {
        key: 'reason_of_eligibility',
        value: data.reason_of_eligibility,
      },
    ] as const;

    if (data.special_rates_and_conditions === 'applicable_specify_below') {
      for (const field of conditionalFields) {
        if (G.isNullable(field.value)) {
          ctx.addIssue({
            code: 'custom',
            path: [field.key],
            message: 'Required when special rates and conditions are applicable',
          });
        }
      }
    }

    if (data.special_rates_and_conditions === 'not_applicable') {
      for (const field of conditionalFields) {
        if (G.isNotNullable(field.value)) {
          ctx.addIssue({
            code: 'custom',
            path: [field.key],
            message: 'Must be null when special rates and conditions are not applicable',
          });
        }
      }
    }
  });

export const ZodCreateBusinessCustomerCompany = z.object({
  name: ZodTrimmedString,
  bank_account: ZodSEPABankAccount.optional(),
  invest_index: z.number().int(),
  revenue_index: z.number().int(),
  investment: ZodBusinessCustomerInvestment.optional(),
  total_assets_savings_index: z.number().int().min(0).max(3).nullish(),
  legal_form: ZodTrimmedString,
  legal_category: ZodTrimmedString.optional(),
  industry: ZodCompanyIndustry,
  siret: ZodTrimmedString.length(14),
  siren: ZodTrimmedString.length(9),
  registration_date: ZodDateString,
  naf_code: ZodTrimmedString,
  address: ZodAddressInfo,
  mailing_address: ZodAddressInfo.optional(),
  lei_code: ZodTrimmedString.optional(),
  directors: ZodCreateBusinessUserCompanyDirectors,
  ubos: z
    .object({
      is_confirmed: z.boolean(),
      items: ZodCreateBusinessUserCompanyUBOs,
    })
    .optional(),
  ubos_v2: UBOsV2Data.optional(),
  us_tax_withholding_and_reporting: ZodWMBusinessUsTaxWithholdingAndReporting.optional(),
});

export const ZodCreateBusinessCustomerBody = z
  .object({
    account_holder: ZodCreateBusinessCustomerAccountHolder.superRefine(customNationalityValidation),
    company: ZodCreateBusinessCustomerCompany,
    draft_id: z.number().optional(),
  })
  .refine((data) => data.company.siren === data.company.siret.substring(0, 9), {
    message: 'SIREN must be the first 9 characters of SIRET',
    path: ['company.siren'],
  });

export type CreateBusinessCustomerPayload = z.infer<typeof ZodCreateBusinessCustomerBody>;

export const postCreateBusinessCustomerEndpoint = endpoint({
  method: 'POST',
  body: ZodCreateBusinessCustomerBody,
  responseBody: ZodCreateCustomerResponse,
});

export type PostCreateBusinessCustomerEndpoint = GetEndpointType<
  typeof postCreateBusinessCustomerEndpoint
>;
